import { MAX_DECIMAL_SCALE } from '../../../utils/numberPrecision';
import { isNullableNumber } from '../widgetUtils';

import { NumberBoundsSchema } from './numberSchema';

/**
 * Mantine NumberInput only supports inclusive min/max. Exclusive JSON Schema
 * bounds are converted to the next representable inclusive step so clampBehavior
 * can enforce gt/lt/positive/negative constraints in the UI.
 */
export function resolveNumberInputBounds(
  schema: NumberBoundsSchema,
  digits?: number
): { min?: number; max?: number } {
  const allowDecimal = isNullableNumber(schema.type);
  const decimalScale = Math.min(digits ?? MAX_DECIMAL_SCALE, MAX_DECIMAL_SCALE);
  const mins: number[] = [];
  const maxs: number[] = [];

  if (typeof schema.minimum === 'number') {
    mins.push(schema.minimum);
  }
  if (typeof schema.exclusiveMinimum === 'number') {
    mins.push(
      allowDecimal
        ? nextAbove(schema.exclusiveMinimum, decimalScale)
        : schema.exclusiveMinimum + 1
    );
  }

  if (typeof schema.maximum === 'number') {
    maxs.push(schema.maximum);
  }
  if (typeof schema.exclusiveMaximum === 'number') {
    maxs.push(
      allowDecimal
        ? nextBelow(schema.exclusiveMaximum, decimalScale)
        : schema.exclusiveMaximum - 1
    );
  }

  return {
    min: mins.length > 0 ? Math.max(...mins) : undefined,
    max: maxs.length > 0 ? Math.min(...maxs) : undefined,
  };
}

function nextAbove(value: number, decimalScale: number): number {
  const factor = 10 ** decimalScale;
  return (Math.floor(value * factor) + 1) / factor;
}

function nextBelow(value: number, decimalScale: number): number {
  const factor = 10 ** decimalScale;
  return (Math.ceil(value * factor) - 1) / factor;
}
